import React, { useState, useEffect } from "react";
import {
  Headset,
  Truck,
  CreditCard,
  Headphones,
  Menu,
  X,
  ArrowRight,
  MapPin,
  Phone,
  Mail,
  CheckCircle2,
  MemoryStick,
  HardDrive,
  Keyboard,
  Mouse,
  Cable,
  Plug,
} from "lucide-react";
import {
  company,
  navConfig,
  categoryConfig,
  partsConfig,
  featuresConfig,
  heroImages,
  aboutVideo,
  contactInfo,
} from "../mock";
import { waLink } from "./WhatsApp";
import { useLang, LanguageSwitcher } from "../i18n";
import { track } from "../analytics";
import { SubscribeForm } from "./Subscribe";
import { API, resolveImage } from "../products";
import { useSiteMedia } from "../siteMedia";

const featureIconMap = { Truck, CreditCard, Headphones };
const partIconMap = { MemoryStick, HardDrive, Keyboard, Mouse, Cable, Plug };

export const Logo = ({ light = false }) => (
  <span className="inline-flex items-center select-none">
    <img
      src={light ? "/uploads/tcs_logo_white.png" : "/uploads/tcs_logo.png"}
      alt="Tele-Comp Solutions"
      className="h-14 w-auto object-contain"
    />
  </span>
);

export const TopBar = () => {
  const { t } = useLang();
  return (
    <div className="w-full bg-slate-900 text-slate-200">
      <div className="max-w-[1280px] mx-auto px-6 py-3 flex items-center justify-between gap-4">
        <p className="text-sm md:text-[15px] font-medium flex-1 text-center sm:text-left">
          {t.announcement}
        </p>
        <div className="shrink-0">
          <LanguageSwitcher dark />
        </div>
      </div>
    </div>
  );
};

export const Header = () => {
  const { t } = useLang();
  return (
    <header className="w-full bg-white border-b border-slate-100 sticky top-0 z-40">
      <div className="max-w-[1280px] mx-auto px-6 py-5 flex items-center justify-between gap-6">
        <a href="/#home" className="shrink-0">
          <Logo />
        </a>
        <div className="flex items-center gap-5 shrink-0">
          <a
            href={`tel:${company.phone}`}
            className="hidden sm:flex items-center gap-2 text-slate-800 hover:text-green-600 transition-colors"
          >
            <Headset className="w-6 h-6 text-green-600" />
            <span className="font-bold text-lg">{company.phone}</span>
          </a>
          <a
            href={waLink(t.whatsapp.defaultMessage)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("quote_click", { label: "header" })}
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-green-600 hover:bg-green-700 text-white font-bold transition-colors"
          >
            {t.getQuote}
          </a>
        </div>
      </div>
    </header>
  );
};

export const NavBar = () => {
  const { t } = useLang();
  const [open, setOpen] = React.useState(false);
  return (
    <nav className="w-full bg-white border-b border-slate-100">
      <div className="max-w-[1280px] mx-auto px-6 py-4 flex items-center gap-8">
        <button
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          aria-expanded={open}
          className="md:hidden text-slate-700"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        <ul className="hidden md:flex items-center gap-8">
          {navConfig.map((link) => (
            <li key={link.key}>
              <a
                href={link.href}
                className="text-[15px] font-bold tracking-wide text-slate-800 hover:text-green-600 transition-colors relative group"
              >
                {t.nav[link.key]}
                <span className="absolute left-0 -bottom-1 w-0 h-0.5 bg-green-600 group-hover:w-full transition-all duration-300" />
              </a>
            </li>
          ))}
        </ul>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-100 bg-white">
          <ul className="px-6 py-3">
            {navConfig.map((link) => (
              <li key={link.key} className="border-b border-slate-50 last:border-0">
                <a
                  href={link.href}
                  onClick={() => setOpen(false)}
                  className="block py-3 text-[15px] font-bold tracking-wide text-slate-800 hover:text-green-600 transition-colors"
                >
                  {t.nav[link.key]}
                </a>
              </li>
            ))}
          </ul>
          <div className="px-6 pb-5 flex flex-col gap-3">
            <a
              href={`tel:${company.phone}`}
              className="flex items-center justify-center gap-2 px-5 py-3 rounded-full border-2 border-slate-200 text-slate-800 font-bold"
            >
              <Headset className="w-5 h-5 text-green-600" /> {company.phone}
            </a>
            <a
              href={waLink(t.whatsapp.defaultMessage)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                track("quote_click", { label: "mobile" });
                setOpen(false);
              }}
              className="flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-green-600 hover:bg-green-700 text-white font-bold transition-colors"
            >
              {t.getQuote}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export const AboutSection = () => {
  const { t } = useLang();
  const a = t.about;
  const media = useSiteMedia();
  const videoSrc = media.about_video ? resolveImage(media.about_video) : aboutVideo;
  const posterSrc = media.about_poster ? resolveImage(media.about_poster) : heroImages.about;
  return (
    <section id="about" className="bg-slate-50 border-y border-slate-100">
      <div className="max-w-[1280px] mx-auto px-6 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
        <div className="relative order-2 lg:order-1">
          <video
            key={videoSrc}
            src={videoSrc}
            poster={posterSrc}
            controls
            playsInline
            preload="metadata"
            className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3] bg-slate-900"
          />
          <div className="absolute -bottom-6 -right-6 bg-green-600 text-white rounded-2xl shadow-xl p-5 pointer-events-none">
            <div className="text-3xl font-extrabold">{a.estLabel}</div>
            <div className="text-sm text-green-100">{a.estCity}</div>
          </div>
        </div>
        <div className="order-1 lg:order-2">
          <span className="text-green-600 font-bold uppercase tracking-widest text-sm">
            {a.eyebrow}
          </span>
          <h2 className="mt-2 text-3xl md:text-4xl font-extrabold text-slate-800 leading-tight">
            {a.title}
          </h2>
          <p className="mt-5 text-slate-500 leading-relaxed">{a.p1}</p>
          <p className="mt-3 text-slate-500 leading-relaxed">{a.p2}</p>
          <ul className="mt-6 space-y-3">
            {a.bullets.map((item) => (
              <li key={item} className="flex items-center gap-3 text-slate-700">
                <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                {item}
              </li>
            ))}
          </ul>
          <a
            href="/catalog"
            className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-green-600 hover:bg-green-700 text-white font-bold shadow-lg shadow-green-600/20 transition-all hover:-translate-y-0.5"
          >
            {a.cta} <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  );
};

export const ShopByCategory = () => {
  const { t, lang } = useLang();
  const p = t.products;
  // Admin-managed tiles; fall back to the static config until loaded / if the API fails.
  const [cats, setCats] = useState(null);
  useEffect(() => {
    fetch(`${API}/site/categories`)
      .then((r) => r.json())
      .then((data) => Array.isArray(data) && data.length ? setCats(data) : setCats(null))
      .catch(() => setCats(null));
  }, []);

  const tiles = cats
    ? cats.map((c) => ({
        key: c.id,
        link: c.link_category || "all",
        image: resolveImage(c.image),
        label: (lang === "es" ? c.name_es : c.name_en) || c.name_en || c.name_es,
      }))
    : categoryConfig.map((c) => ({
        key: c.id,
        link: c.id,
        image: c.image,
        label: t.categories[c.id],
      }));

  return (
    <section id="products" className="max-w-[1280px] mx-auto px-6 py-16">
      <div className="text-center mb-12">
        <span className="text-green-600 font-bold uppercase tracking-widest text-sm">
          {p.eyebrow}
        </span>
        <h2 className="mt-2 text-3xl md:text-4xl font-extrabold text-slate-800">
          {p.title}
        </h2>
        <p className="mt-3 text-slate-500 max-w-2xl mx-auto">{p.sub}</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {tiles.map((cat) => (
          <a
            key={cat.key}
            href={`/catalog?category=${cat.link}`}
            data-testid={`home-category-${cat.link}`}
            className="group relative rounded-2xl overflow-hidden bg-slate-100 aspect-[3/4] shadow-sm hover:shadow-xl transition-all duration-300"
          >
            <img
              src={cat.image}
              alt={cat.label}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5">
              <h3 className="text-white font-bold text-lg">{cat.label}</h3>
            </div>
          </a>
        ))}
      </div>

      <div className="text-center mt-20 mb-10">
        <span className="text-green-600 font-bold uppercase tracking-widest text-sm">
          {p.partsEyebrow}
        </span>
        <h2 className="mt-2 text-3xl md:text-4xl font-extrabold text-slate-800">
          {p.partsTitle}
        </h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {partsConfig.map((part) => {
          const Icon = partIconMap[part.icon];
          return (
            <div
              key={part.id}
              className="group flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 hover:border-green-300 hover:shadow-md transition-all duration-300"
            >
              <div className="shrink-0 w-14 h-14 rounded-xl bg-green-600/10 flex items-center justify-center group-hover:bg-green-600 transition-colors">
                {Icon && (
                  <Icon className="w-7 h-7 text-green-600 group-hover:text-white transition-colors" />
                )}
              </div>
              <div className="min-w-0">
                <h3 className="font-bold text-lg text-slate-800 leading-tight">
                  {t.parts[part.id]}
                </h3>
                <p className="text-sm text-slate-500 mt-1">{t.partsDesc[part.id]}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export const FeaturesStrip = () => {
  const { t } = useLang();
  return (
    <section className="bg-slate-50 border-y border-slate-100">
      <div className="max-w-[1280px] mx-auto px-6 py-14 grid md:grid-cols-3 gap-8">
        {featuresConfig.map((f) => {
          const Icon = featureIconMap[f.icon];
          const item = t.features[f.id];
          return (
            <div
              key={f.id}
              className="flex items-start gap-4 p-6 rounded-2xl bg-white border border-slate-100 hover:border-green-200 hover:shadow-md transition-all duration-300"
            >
              <div className="shrink-0 w-14 h-14 rounded-xl bg-green-600/10 flex items-center justify-center">
                <Icon className="w-7 h-7 text-green-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-slate-800">{item.title}</h3>
                <p className="text-slate-500 mt-1">{item.desc}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export const Footer = () => {
  const { t } = useLang();
  const f = t.footer;
  return (
    <footer className="bg-slate-900 text-slate-300">
      <div className="max-w-[1280px] mx-auto px-6 pt-14">
        <div id="newsletter" className="grid lg:grid-cols-2 gap-6 items-center pb-12 mb-4 border-b border-slate-800 scroll-mt-24">
          <div>
            <h4 className="text-white text-xl font-extrabold">{t.subscribe.title}</h4>
            <p className="mt-1.5 text-slate-400 text-sm">{t.subscribe.sub}</p>
          </div>
          <SubscribeForm variant="light" />
        </div>
      </div>
      <div className="max-w-[1280px] mx-auto px-6 pb-14 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <Logo light />
          <p className="mt-4 max-w-md text-slate-400 leading-relaxed">{f.desc}</p>
        </div>
        <div>
          <h4 className="text-white font-bold mb-4">{f.quickLinks}</h4>
          <ul className="space-y-2">
            {navConfig.map((l) => (
              <li key={l.key}>
                <a href={l.href} className="hover:text-green-500 transition-colors">
                  {t.nav[l.key]}
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-white font-bold mb-4">{f.getInTouch}</h4>
          <ul className="space-y-3 text-slate-400">
            <li className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-green-500" /> {company.phone}
            </li>
            <li className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-green-500" /> {f.locationVal}
            </li>
            <li className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-green-500" /> {contactInfo.email}
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-800">
        <div className="max-w-[1280px] mx-auto px-6 py-5 text-sm text-slate-500 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span>© {new Date().getFullYear()} Telecomp Solutions, LLC. {f.rights}</span>
          <div className="flex items-center gap-5">
            <a href="/privacy" className="hover:text-green-500 transition-colors">
              {f.privacy}
            </a>
            <a href="/terms" className="hover:text-green-500 transition-colors">
              {f.terms}
            </a>
            <span className="hidden sm:inline">{f.tagline}</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
